// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/services/condicionalService.ts
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from '@/lib/apiClient';
import { paramsDaLista, type ListaFiltros, type PaginadoComResumo, type CondicionalListaResumo } from './listaFiltros';
import type {
  CondicionalResumo,
  CondicionalDetalhe,
  CriarCondicionalRequest,
  AtualizarCondicionalRequest,
  RegistrarDevolucaoRequest,
  AcertoCondicionalRequest,
  CondicionalExpiracaoResultado,
} from '../types';
import { paramsConsulta, type ComVendedor, type FiltroConsultaOperacional } from '../consultas';

const BASE = '/api/condicionais';

/** KPIs de GET /api/condicionais/resumo — consulta inteira, sem o status (status exclusivos). */
export interface CondicionalKpi {
  total: number;
  abertas: number;
  valorAbertas: number;
  vendidas: number;
  devolvidas: number;
  vencidas: number;
  expiradas: number;
  valorTotal: number;
}

export const condicionalService = {
  /**
   * Lista paginada + `resumo` (KPIs v118). Aceita os filtros da v118 (`decrescente`) e os da
   * paridade (`direcao` e filtros extras) — o servidor entende os dois.
   */
  async getAll(pagina = 1, tamanhoPagina = 20, filtros?: FiltroConsultaOperacional | ListaFiltros): Promise<PaginadoComResumo<ComVendedor<CondicionalResumo>, CondicionalListaResumo>> {
    const { data } = await apiClient.get<PaginadoComResumo<ComVendedor<CondicionalResumo>, CondicionalListaResumo>>(BASE, {
      params: { ...paramsDaLista(pagina, tamanhoPagina), ...paramsConsulta(filtros as FiltroConsultaOperacional) },
    });
    return data;
  },

  /** KPIs sobre o filtro inteiro (ignora status). */
  async getResumo(filtros?: FiltroConsultaOperacional): Promise<CondicionalKpi> {
    const { status: _s, ordenarPor: _o, direcao: _d, ...resto } = filtros ?? {};
    const { data } = await apiClient.get<CondicionalKpi>(`${BASE}/resumo`, { params: paramsConsulta(resto) });
    return data;
  },

  async getAbertas(): Promise<CondicionalResumo[]> {
    const { data } = await apiClient.get<CondicionalResumo[]>(`${BASE}/abertas`);
    return data;
  },

  async getVencidas(): Promise<CondicionalResumo[]> {
    const { data } = await apiClient.get<CondicionalResumo[]>(`${BASE}/vencidas`);
    return data;
  },

  async expirarVencidas(): Promise<CondicionalExpiracaoResultado> {
    const { data } = await apiClient.post<CondicionalExpiracaoResultado>(`${BASE}/expirar-vencidas`);
    return data;
  },

  async getById(id: number): Promise<CondicionalDetalhe> {
    const { data } = await apiClient.get<CondicionalDetalhe>(`${BASE}/${id}`);
    return data;
  },

  async create(request: CriarCondicionalRequest): Promise<CondicionalDetalhe> {
    const { data } = await apiClient.post<CondicionalDetalhe>(BASE, request);
    return data;
  },

  async update(id: number, request: AtualizarCondicionalRequest): Promise<CondicionalResumo> {
    const { data } = await apiClient.put<CondicionalResumo>(`${BASE}/${id}`, request);
    return data;
  },

  async registrarDevolucao(id: number, request: RegistrarDevolucaoRequest): Promise<CondicionalDetalhe> {
    const { data } = await apiClient.put<CondicionalDetalhe>(`${BASE}/${id}/devolucao`, request);
    return data;
  },

  async acertar(id: number, request: AcertoCondicionalRequest): Promise<CondicionalDetalhe> {
    const { data } = await apiClient.put<CondicionalDetalhe>(`${BASE}/${id}/acerto`, request);
    return data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
